import './TechStackBar.css';
import { useEffect, useRef, useState } from 'react';

const defaultTechStack = [
  { name: 'React', file: 'react.svg' },
  { name: 'JavaScript', file: 'javascript.svg' },
  { name: 'Node.js', file: 'nodejs.svg' },
];

const defaultGroups = [{ title: '', items: defaultTechStack }];

function TechStackBar({
  items = defaultTechStack,
  groups,
  iconBasePath = '/assets/tech',
  size = 28,
  showLabels = false,
  onHeightChange,
  onToggle,
}) {
  const resolvedGroups = groups ?? defaultGroups;
  const rootRef = useRef(null);
  const [isMobile, setIsMobile] = useState(false);
  const [expanded, setExpanded] = useState(true);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 720px)');

    const apply = () => {
      const mobile = mq.matches;
      setIsMobile(mobile);
      setExpanded(!mobile);
    };

    apply();
    if (mq.addEventListener) mq.addEventListener('change', apply);
    else mq.addListener(apply);

    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', apply);
      else mq.removeListener(apply);
    };
  }, []);

  useEffect(() => {
    if (!onToggle) return;
    onToggle({ isMobile, expanded });
  }, [onToggle, isMobile, expanded]);

  useEffect(() => {
    const el = rootRef.current;
    if (!el || !onHeightChange) return;

    const notify = () => {
      onHeightChange(el.getBoundingClientRect().height);
    };

    notify();
    const ro = new ResizeObserver(() => notify());
    ro.observe(el);
    return () => ro.disconnect();
  }, [onHeightChange, isMobile, expanded, groups, items]);

  const renderItems = (groupTitle, groupItems) => (
    <div className="tech-stack-bar__group-items">
      {groupItems.map((item) => (
        <div key={`${groupTitle}-${item.name}`} className="tech-stack-bar__item" title={item.name}>
          <img
            className="tech-stack-bar__icon"
            src={item.src ?? `${iconBasePath}/${item.file}`}
            alt={item.name}
            style={{ width: size, height: size }}
            loading="lazy"
            onError={(e) => {
              e.currentTarget.style.display = 'none';
            }}
          />
          <span className="tech-stack-bar__label" data-visible={showLabels ? 'true' : 'false'}>
            {item.name}
          </span>
        </div>
      ))}
    </div>
  );

  return (
    <div
      ref={rootRef}
      className={`tech-stack-bar${isMobile ? ' tech-stack-bar--mobile' : ''}${isMobile && expanded ? ' tech-stack-bar--expanded' : ''}`}
      aria-label="Tech stack"
    >
      {isMobile ? (
        <button
          type="button"
          className="tech-stack-bar__toggle"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
          aria-label="展开技术栈"
          title="展开技术栈"
        >
          <span className={`tech-stack-bar__chevron${expanded ? ' tech-stack-bar__chevron--up' : ''}`} aria-hidden="true">
            ▾
          </span>
        </button>
      ) : null}

      <div className={`tech-stack-bar__inner${isMobile && !expanded ? ' tech-stack-bar__inner--collapsed' : ''}`}>
        {(groups ? resolvedGroups : [{ title: '', items }]).map((group) => (
          <div key={group.title} className="tech-stack-bar__group">
            {group.title ? <div className="tech-stack-bar__group-title">{group.title}</div> : null}
            {group.columns ? (
              <div className="tech-stack-bar__columns tech-stack-bar__columns--inline">
                {group.columns.map((col, idx) => (
                  <div
                    key={`${group.title}-${col.title}`}
                    className={`tech-stack-bar__column-row tech-stack-bar__column-row--inline${idx > 0 ? ' tech-stack-bar__column-row--divider' : ''}`}
                  >
                    {renderItems(`${group.title}-${col.title}`, col.items)}
                  </div>
                ))}
              </div>
            ) : (
              renderItems(group.title, group.items)
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export default TechStackBar;
